// src/components/ProfileScreen.jsx
// Rota: /profile
//
// GET  /psy/user/me
// PUT  /psy/user/me
//
// O usuário é identificado exclusivamente pelo token JWT.
// Não depende do ID armazenado no navegador.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  FileCheck2,
  GraduationCap,
  House,
  IdCard,
  LockKeyhole,
  Mail,
  Map,
  MapPin,
  Menu,
  LoaderCircle,
  Phone,
  RefreshCw,
  Save,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/profile.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const PROFILE_ENDPOINT = `${API_BASE}/psy/user/me`;

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
};

function readStorage(keys = []) {
  for (const key of keys) {
    const sessionValue = sessionStorage.getItem(key);

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }

    const localValue = localStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }
  }

  return "";
}

function writeToken(token) {
  const normalizedToken = String(token || "").trim();

  if (!normalizedToken) return;

  try {
    sessionStorage.setItem(AUTH_KEYS.TOKEN, normalizedToken);
    sessionStorage.setItem("token", normalizedToken);
    localStorage.setItem(AUTH_KEYS.TOKEN, normalizedToken);
  } catch (error) {
    console.warn("Não foi possível atualizar o token:", error);
  }
}

function writeUserName(name) {
  const normalizedName = String(name || "").trim();

  if (!normalizedName) return;

  try {
    sessionStorage.setItem(AUTH_KEYS.USER_NAME, normalizedName);
    localStorage.setItem(AUTH_KEYS.USER_NAME, normalizedName);
  } catch (error) {
    console.warn("Não foi possível atualizar o nome no storage:", error);
  }
}

function clearAuthentication() {
  try {
    sessionStorage.removeItem(AUTH_KEYS.TOKEN);
    sessionStorage.removeItem("token");

    localStorage.removeItem(AUTH_KEYS.TOKEN);
    localStorage.removeItem("token");
  } catch (error) {
    console.warn("Não foi possível limpar a autenticação:", error);
  }
}

function getFirstName(name = "") {
  const normalizedName = String(name || "").trim();

  if (!normalizedName) return "";

  return normalizedName.split(/\s+/)[0];
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "PR";

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function onlyNumbers(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatPhone(value = "") {
  const numbers = onlyNumbers(value).slice(0, 11);

  if (numbers.length <= 2) return numbers;

  if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  }

  if (numbers.length <= 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(
      2,
      6
    )}-${numbers.slice(6)}`;
  }

  return `(${numbers.slice(0, 2)}) ${numbers.slice(
    2,
    7
  )}-${numbers.slice(7)}`;
}

function formatCep(value = "") {
  const numbers = onlyNumbers(value).slice(0, 8);

  if (numbers.length <= 5) return numbers;

  return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
}

function formatDate(value, fallback = "Não informado") {
  if (!value) return fallback;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return fallback;

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "medium",
    timeZone: "America/Sao_Paulo",
  }).format(date);
}

function translateStatus(status = "") {
  const normalized = String(status || "").trim().toLowerCase();

  const labels = {
    trialing: "Período de teste",
    active: "Ativa",
    past_due: "Pagamento pendente",
    canceled: "Cancelada",
    unpaid: "Não paga",
    incomplete: "Incompleta",
    incomplete_expired: "Incompleta e expirada",
    paused: "Pausada",
  };

  return labels[normalized] || status || "Não informado";
}

function translateAccountType(type = "") {
  const normalized = String(type || "").trim().toLowerCase();

  if (normalized === "clinica") return "Clínica";

  return "Profissional individual";
}

function translatePermission(permission = "") {
  const normalized = String(permission || "").trim().toLowerCase();

  if (normalized === "admin") return "Administrador";

  return "Usuário";
}

function translateOrigin(origin = "") {
  const normalized = String(origin || "").trim().toLowerCase();

  const labels = {
    "landing-page": "Landing page",
    landing_page: "Landing page",
    manual: "Cadastro manual",
    app: "Aplicativo",
    web: "Sistema web",
  };

  return labels[normalized] || origin || "Não informada";
}

async function readResponsePayload(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return await response.json();
    }

    const text = await response.text();

    return text ? { message: text } : {};
  } catch {
    return {};
  }
}

function EditableField({
  label,
  value,
  onChange,
  icon,
  placeholder,
  error,
  disabled = false,
  autoComplete = "off",
  maxLength,
}) {
  return (
    <label className="pro-field">
      <span className="pro-field__lbl">{label}</span>

      <div
        className={`pro-inputwrap ${
          error ? "is-invalid" : ""
        }`}
      >
        <span className="pro-ico" aria-hidden="true">
          {icon}
        </span>

        <input
          className="pro-input"
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          aria-invalid={Boolean(error)}
        />
      </div>

      {error ? (
        <span className="pro-field__err">{error}</span>
      ) : null}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  icon,
  options = [],
  error,
  disabled = false,
}) {
  return (
    <label className="pro-field">
      <span className="pro-field__lbl">{label}</span>

      <div
        className={`pro-inputwrap ${
          error ? "is-invalid" : ""
        }`}
      >
        <span className="pro-ico" aria-hidden="true">
          {icon}
        </span>

        <select
          className="pro-input"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
        >
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <span className="pro-field__err">{error}</span>
      ) : null}
    </label>
  );
}

function LockedField({
  label,
  value,
  icon,
  helper,
}) {
  return (
    <div className="pro-field pro-field--locked">
      <span className="pro-field__lbl">{label}</span>

      <div className="pro-inputwrap pro-inputwrap--locked">
        <span className="pro-ico" aria-hidden="true">
          {icon}
        </span>

        <div className="pro-locked-value">
          <strong>{value || "Não informado"}</strong>

          {helper ? <span>{helper}</span> : null}
        </div>

        <span
          className="pro-locked-icon"
          title="Campo não editável nesta tela"
          aria-label="Campo bloqueado"
        >
          <LockKeyhole size={16} />
        </span>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="pro-skeleton" aria-hidden="true">
      <div className="pro-skeleton__hero" />
      <div className="pro-skeleton__panel" />
      <div className="pro-skeleton__panel" />
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
}) {
  return (
    <section className="pro-state pro-state--error">
      <div className="pro-state__icon"><AlertCircle size={24} /></div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="pro-state__actions">
        <button
          className="pro-btn pro-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Configurações
        </button>

        <button
          className="pro-btn pro-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

const PIX_TYPES = [
  { value: "", label: "Não configurado" },
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "email", label: "E-mail" },
  { value: "telefone", label: "Telefone" },
  { value: "aleatoria", label: "Chave aleatória" },
];

const INITIAL_FORM = {
  nomeUsuario: "",
  telefone: "",
  crp: "",
  abordagem: "",
  ramoAtuacao: "",
  nomeEstabelecimento: "",
  endereco: "",
  cep: "",
  cidade: "",
  estado: "",
  pais: "",

  pixTipo: "",
  pixChave: "",
  pixFavorecido: "",
};

export default function ProfileScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(INITIAL_FORM);
  const [account, setAccount] = useState({});

  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({
    type: "",
    message: "",
  });

  const [loadError, setLoadError] = useState("");

  const storedName = useMemo(
    () =>
      readStorage([
        AUTH_KEYS.USER_NAME,
        "nomeUsuario",
        "usuario",
      ]),
    []
  );

  const firstName = useMemo(
    () =>
      getFirstName(
        form.nomeUsuario || storedName
      ),
    [form.nomeUsuario, storedName]
  );

  const initials = useMemo(
    () =>
      getInitials(
        form.nomeUsuario || storedName
      ),
    [form.nomeUsuario, storedName]
  );

  function updateField(field, value) {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: "",
    }));

    setFeedback({
      type: "",
      message: "",
    });
  }

  const validate = useCallback(() => {
    const nextErrors = {};

    const name = String(form.nomeUsuario || "").trim();
    const phone = onlyNumbers(form.telefone);
    const professionalArea = String(
      form.ramoAtuacao || ""
    ).trim();

    const state = String(form.estado || "")
      .trim()
      .replace(/[^A-Za-z]/g, "");

    const postalCode = onlyNumbers(form.cep);

    if (!name) {
      nextErrors.nomeUsuario =
        "Informe seu nome completo.";
    } else if (name.length < 3) {
      nextErrors.nomeUsuario =
        "Informe um nome válido.";
    }

    if (!phone) {
      nextErrors.telefone =
        "Informe seu telefone.";
    } else if (
      phone.length < 10 ||
      phone.length > 11
    ) {
      nextErrors.telefone =
        "Informe um telefone válido com DDD.";
    }

    if (!professionalArea) {
      nextErrors.ramoAtuacao =
        "Informe sua área de atuação.";
    }

    if (state && state.length !== 2) {
      nextErrors.estado =
        "Informe a sigla do estado com duas letras.";
    }

    if (postalCode && postalCode.length !== 8) {
      nextErrors.cep =
        "Informe um CEP válido com 8 números.";
    }

    const pixTipo = String(form.pixTipo || "").trim();
    const pixChave = String(form.pixChave || "").trim();
    const pixFavorecido = String(
      form.pixFavorecido || ""
    ).trim();

    const pixPreenchido =
      pixTipo ||
      pixChave ||
      pixFavorecido;

    if (pixPreenchido) {
      if (!pixTipo) {
        nextErrors.pixTipo =
          "Selecione o tipo da chave Pix.";
      }

      if (!pixChave) {
        nextErrors.pixChave =
          "Informe a chave Pix.";
      }

      if (!pixFavorecido) {
        nextErrors.pixFavorecido =
          "Informe o favorecido.";
      }

      if (pixTipo === "cpf") {
        const numeros = onlyNumbers(pixChave);

        if (numeros.length !== 11) {
          nextErrors.pixChave =
            "Informe um CPF válido.";
        }
      }

      if (pixTipo === "cnpj") {
        const numeros = onlyNumbers(pixChave);

        if (numeros.length !== 14) {
          nextErrors.pixChave =
            "Informe um CNPJ válido.";
        }
      }

      if (
        pixTipo === "email" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          pixChave
        )
      ) {
        nextErrors.pixChave =
          "Informe um e-mail válido.";
      }

      if (pixTipo === "telefone") {
        const numeros = onlyNumbers(pixChave);

        if (
          numeros.length < 10 ||
          numeros.length > 13
        ) {
          nextErrors.pixChave =
            "Informe um telefone válido.";
        }
      }

      if (
        pixTipo === "aleatoria" &&
        pixChave.length < 8
      ) {
        nextErrors.pixChave =
          "Informe uma chave aleatória válida.";
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }, [form]);

  const loadUserData = useCallback(
    async (signal) => {
      setIsLoading(true);
      setLoadError("");
      setFeedback({ type: "", message: "" });

      try {
        const token = readStorage([
          AUTH_KEYS.TOKEN,
          "token",
          "accessToken",
        ]);

        if (!token) {
          clearAuthentication();
          navigate("/login", { replace: true });
          return;
        }

        const response = await fetch(
          PROFILE_ENDPOINT,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
            signal,
          }
        );

        writeToken(
          response.headers.get("x-new-token")
        );

        if (
          response.status === 401 ||
          response.status === 403
        ) {
          clearAuthentication();
          navigate("/login", { replace: true });
          return;
        }

        const payload =
          await readResponsePayload(response);

        if (!response.ok) {
          setLoadError(
            payload?.message ||
              `Erro ao carregar o perfil (HTTP ${response.status}).`
          );
          return;
        }

        const user = payload?.data || payload;

        if (!user || !user.id) {
          setLoadError(
            "A API não retornou os dados do usuário."
          );
          return;
        }

        const userName = String(
          user.nomeUsuario ||
            user.nome ||
            ""
        ).trim();

        setForm({
          nomeUsuario: userName,
          telefone: formatPhone(user.telefone || ""),
          crp: String(user.crp || "").trim(),
          abordagem: String(
            user.abordagem || ""
          ).trim(),
          ramoAtuacao: String(
            user.ramoAtuacao || ""
          ).trim(),
          nomeEstabelecimento: String(
            user.nomeEstabelecimento || ""
          ).trim(),
          endereco: String(
            user.endereco || ""
          ).trim(),
          cep: formatCep(user.cep || ""),
          cidade: String(user.cidade || "").trim(),
          estado: String(user.estado || "")
            .trim()
            .toUpperCase(),
          pais: String(user.pais || "").trim(),

          pixTipo: String(
            user.pix?.tipo || ""
          ).trim(),

          pixChave: String(
            user.pix?.chave || ""
          ).trim(),

          pixFavorecido: String(
            user.pix?.favorecido || ""
          ).trim(),
        });

        setAccount(user);

        if (userName) {
          writeUserName(userName);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error(
          "Erro ao carregar perfil:",
          error
        );

        setLoadError(
          "Erro de conexão ao carregar o perfil."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadUserData(controller.signal);

    return () => controller.abort();
  }, [loadUserData]);

  async function saveProfile() {
    setFeedback({
      type: "",
      message: "",
    });

    if (!validate()) {
      setFeedback({
        type: "error",
        message:
          "Revise os campos destacados antes de salvar.",
      });

      return;
    }

    const token = readStorage([
      AUTH_KEYS.TOKEN,
      "token",
      "accessToken",
    ]);

    if (!token) {
      clearAuthentication();

      navigate("/login", {
        replace: true,
      });

      return;
    }

    setSaving(true);

    try {
      const payload = {
        nomeUsuario: String(
          form.nomeUsuario || ""
        ).trim(),

        telefone: String(
          form.telefone || ""
        ).trim(),

        crp: String(form.crp || "").trim(),

        abordagem: String(
          form.abordagem || ""
        ).trim(),

        ramoAtuacao: String(
          form.ramoAtuacao || ""
        ).trim(),

        nomeEstabelecimento: String(
          form.nomeEstabelecimento || ""
        ).trim(),

        endereco: String(
          form.endereco || ""
        ).trim(),

        cep: String(form.cep || "").trim(),

        cidade: String(
          form.cidade || ""
        ).trim(),

        estado: String(
          form.estado || ""
        )
          .trim()
          .toUpperCase(),

        pais: String(form.pais || "").trim(),

        pix: {
          tipo:
            String(form.pixTipo || "").trim() ||
            null,

          chave:
            String(form.pixChave || "").trim() ||
            null,

          favorecido:
            String(form.pixFavorecido || "").trim() ||
            null,
        },
      };

      const response = await fetch(
        PROFILE_ENDPOINT,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      writeToken(
        response.headers.get("x-new-token")
      );

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        clearAuthentication();
        navigate("/login", { replace: true });
        return;
      }

      const result =
        await readResponsePayload(response);

      if (!response.ok) {
        setFeedback({
          type: "error",
          message:
            result?.message ||
            `Erro ao atualizar o perfil (HTTP ${response.status}).`,
        });

        return;
      }

      const updated = result?.data || {};

      setForm((previous) => ({
        ...previous,
        nomeUsuario:
          updated.nomeUsuario ??
          payload.nomeUsuario,

        telefone: formatPhone(
          updated.telefone ??
          payload.telefone
        ),

        crp: updated.crp ?? payload.crp,
        abordagem:
          updated.abordagem ??
          payload.abordagem,

        ramoAtuacao:
          updated.ramoAtuacao ??
          payload.ramoAtuacao,

        nomeEstabelecimento:
          updated.nomeEstabelecimento ??
          payload.nomeEstabelecimento,

        endereco:
          updated.endereco ??
          payload.endereco,

        cep: formatCep(
          updated.cep ??
          payload.cep
        ),

        cidade:
          updated.cidade ??
          payload.cidade,

        estado:
          updated.estado ??
          payload.estado,

        pais:
          updated.pais ??
          payload.pais,

        pixTipo:
          updated.pix?.tipo ??
          payload.pix?.tipo ??
          "",

        pixChave:
          updated.pix?.chave ??
          payload.pix?.chave ??
          "",

        pixFavorecido:
          updated.pix?.favorecido ??
          payload.pix?.favorecido ??
          "",
      }));

      setAccount((previous) => ({
        ...previous,
        ...updated,
      }));

      writeUserName(payload.nomeUsuario);

      setFeedback({
        type: "success",
        message:
          result?.message ||
          "Perfil atualizado com sucesso.",
      });
    } catch (error) {
      console.error(
        "Erro ao atualizar perfil:",
        error
      );

      setFeedback({
        type: "error",
        message:
          "Erro de conexão ao atualizar o perfil.",
      });
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    navigate("/settings");
  }

  function retry() {
    const controller = new AbortController();
    loadUserData(controller.signal);
  }

  return (
    <div className="pro-page">
      <div className="pro-bg" aria-hidden="true">
        <span className="pro-bg__orb pro-bg__orb--one" />
        <span className="pro-bg__orb pro-bg__orb--two" />
        <span className="pro-bg__orb pro-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <header className="pro-topbar">
        <button
          className="pro-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
          title="Abrir menu"
        >
          <Menu size={21} />
        </button>

        <div className="pro-brand">
          <img
            src={Logo}
            alt="PsyAssist Pro"
            className="pro-brand__logo"
          />

          <div className="pro-brand__text">
            <strong>Perfil</strong>

            <span>
              Dados cadastrais •{" "}
              {form.nomeUsuario ||
                storedName ||
                "Usuário"}
            </span>
          </div>
        </div>

        <div className="pro-top-actions">
          <button
            className="pro-btn pro-btn--ghost pro-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft size={16} />
            Configurações
          </button>

          <button
            className="pro-btn pro-btn--primary"
            type="button"
            onClick={saveProfile}
            disabled={
              isLoading ||
              saving ||
              Boolean(loadError)
            }
          >
            {saving ? (
              <>
                <LoaderCircle size={16} className="pro-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar
              </>
            )}
          </button>
        </div>
      </header>

      <main className="pro-main">
        {isLoading ? (
          <ProfileSkeleton />
        ) : loadError ? (
          <EmptyState
            title="Não foi possível carregar o perfil."
            message={loadError}
            onRetry={retry}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="pro-hero">
              <div>
                <span className="pro-kicker">
                  <UserRound size={14} />
                  Dados profissionais
                </span>

                <h1>Meu perfil</h1>

                <p>
                  {firstName
                    ? `${firstName}, `
                    : ""}
                  mantenha seus dados atualizados para
                  que a plataforma apresente suas
                  informações corretamente.
                </p>
              </div>

              <aside className="pro-hero-card">
                <div
                  className="pro-hero-card__avatar"
                  aria-hidden="true"
                >
                  {initials}
                </div>

                <div>
                  <small>
                    {account.ativo === false
                      ? "Conta inativa"
                      : "Conta ativa"}
                  </small>

                  <strong>
                    {form.nomeUsuario || "Usuário"}
                  </strong>

                  <span>
                    {form.ramoAtuacao ||
                      "Área de atuação não informada"}
                  </span>
                </div>
              </aside>
            </section>

            <section className="pro-panel">
              <div className="pro-panel__head">
                <div>
                  <span>Profissional</span>
                  <h2>Dados profissionais</h2>

                  <p>
                    Informações utilizadas na sua conta
                    e na identificação profissional.
                  </p>
                </div>

                <div className="pro-mode">
                  Editável
                </div>
              </div>

              {feedback.message ? (
                <div
                  className={`pro-feedback pro-feedback--${
                    feedback.type || "info"
                  }`}
                  role={
                    feedback.type === "error"
                      ? "alert"
                      : "status"
                  }
                >
                  {feedback.message}
                </div>
              ) : null}

              <div className="pro-form">
                <EditableField
                  label="Nome completo"
                  value={form.nomeUsuario}
                  onChange={(value) =>
                    updateField(
                      "nomeUsuario",
                      value
                    )
                  }
                  placeholder="Seu nome completo"
                  error={errors.nomeUsuario}
                  icon={<UserRound size={18} />}
                  disabled={saving}
                  autoComplete="name"
                />

                <EditableField
                  label="Telefone"
                  value={form.telefone}
                  onChange={(value) =>
                    updateField(
                      "telefone",
                      formatPhone(value)
                    )
                  }
                  placeholder="(11) 99999-9999"
                  error={errors.telefone}
                  icon={<Phone size={18} />}
                  disabled={saving}
                  autoComplete="tel"
                />

                <EditableField
                  label="CRP"
                  value={form.crp}
                  onChange={(value) =>
                    updateField("crp", value)
                  }
                  placeholder="Ex.: 06/123456"
                  error={errors.crp}
                  icon={<IdCard size={18} />}
                  disabled={saving}
                  maxLength={30}
                />

                <EditableField
                  label="Abordagem"
                  value={form.abordagem}
                  onChange={(value) =>
                    updateField(
                      "abordagem",
                      value
                    )
                  }
                  placeholder="Ex.: Fenomenologia"
                  error={errors.abordagem}
                  icon={
                    <GraduationCap size={18} />
                  }
                  disabled={saving}
                />

                <EditableField
                  label="Área de atuação"
                  value={form.ramoAtuacao}
                  onChange={(value) =>
                    updateField(
                      "ramoAtuacao",
                      value
                    )
                  }
                  placeholder="Ex.: Psicologia clínica"
                  error={errors.ramoAtuacao}
                  icon={
                    <BriefcaseBusiness size={18} />
                  }
                  disabled={saving}
                />

                <EditableField
                  label="Nome do consultório"
                  value={
                    form.nomeEstabelecimento
                  }
                  onChange={(value) =>
                    updateField(
                      "nomeEstabelecimento",
                      value
                    )
                  }
                  placeholder="Nome profissional ou consultório"
                  error={
                    errors.nomeEstabelecimento
                  }
                  icon={<Building2 size={18} />}
                  disabled={saving}
                />
              </div>
            </section>

            <section className="pro-panel">
              <div className="pro-panel__head">
                <div>
                  <span>Localização</span>
                  <h2>Endereço</h2>

                  <p>
                    Dados de localização utilizados nos
                    recursos administrativos.
                  </p>
                </div>

                <div className="pro-mode">
                  Editável
                </div>
              </div>

              <div className="pro-form">
                <EditableField
                  label="Endereço"
                  value={form.endereco}
                  onChange={(value) =>
                    updateField(
                      "endereco",
                      value
                    )
                  }
                  placeholder="Rua, número e complemento"
                  error={errors.endereco}
                  icon={<House size={18} />}
                  disabled={saving}
                  autoComplete="street-address"
                />

                <EditableField
                  label="CEP"
                  value={form.cep}
                  onChange={(value) =>
                    updateField(
                      "cep",
                      formatCep(value)
                    )
                  }
                  placeholder="00000-000"
                  error={errors.cep}
                  icon={<MapPin size={18} />}
                  disabled={saving}
                  autoComplete="postal-code"
                />

                <EditableField
                  label="Cidade"
                  value={form.cidade}
                  onChange={(value) =>
                    updateField(
                      "cidade",
                      value
                    )
                  }
                  placeholder="Sua cidade"
                  error={errors.cidade}
                  icon={<Building2 size={18} />}
                  disabled={saving}
                  autoComplete="address-level2"
                />

                <EditableField
                  label="Estado"
                  value={form.estado}
                  onChange={(value) =>
                    updateField(
                      "estado",
                      value
                        .replace(
                          /[^A-Za-z]/g,
                          ""
                        )
                        .slice(0, 2)
                        .toUpperCase()
                    )
                  }
                  placeholder="SP"
                  error={errors.estado}
                  icon={<Map size={18} />}
                  disabled={saving}
                  maxLength={2}
                  autoComplete="address-level1"
                />

                <EditableField
                  label="País"
                  value={form.pais}
                  onChange={(value) =>
                    updateField("pais", value)
                  }
                  placeholder="Brasil"
                  error={errors.pais}
                  icon={<MapPin size={18} />}
                  disabled={saving}
                  autoComplete="country-name"
                />
              </div>
            </section>

            <section className="pro-panel">
              <div className="pro-panel__head">
                <div>
                  <span>Recebimentos</span>
                  <h2>Recebimentos via Pix</h2>

                  <p>
                    Configure os dados utilizados nas
                    comunicações de cobrança. A chave Pix
                    e o favorecido poderão ser enviados ao
                    cliente junto das pendências financeiras.
                  </p>
                </div>

                <div className="pro-mode">
                  Editável
                </div>
              </div>

              <div className="pro-form">
                <SelectField
                  label="Tipo da chave Pix"
                  value={form.pixTipo}
                  onChange={(value) =>
                    updateField("pixTipo", value)
                  }
                  options={PIX_TYPES}
                  error={errors.pixTipo}
                  icon={
                    <CircleDollarSign size={18} />
                  }
                  disabled={saving}
                />

                <EditableField
                  label="Chave Pix"
                  value={form.pixChave}
                  onChange={(value) =>
                    updateField("pixChave", value)
                  }
                  placeholder={
                    form.pixTipo === "cpf"
                      ? "000.000.000-00"
                      : form.pixTipo === "cnpj"
                        ? "00.000.000/0000-00"
                        : form.pixTipo === "email"
                          ? "financeiro@exemplo.com"
                          : form.pixTipo === "telefone"
                            ? "(11) 99999-9999"
                            : form.pixTipo === "aleatoria"
                              ? "Chave aleatória"
                              : "Informe sua chave Pix"
                  }
                  error={errors.pixChave}
                  icon={<IdCard size={18} />}
                  disabled={saving}
                  autoComplete="off"
                />

                <EditableField
                  label="Favorecido"
                  value={form.pixFavorecido}
                  onChange={(value) =>
                    updateField(
                      "pixFavorecido",
                      value
                    )
                  }
                  placeholder="Nome do titular da chave"
                  error={errors.pixFavorecido}
                  icon={<UserRound size={18} />}
                  disabled={saving}
                  autoComplete="name"
                />
              </div>

              <div
                className="pro-feedback pro-feedback--info"
                role="note"
              >
                O Pix é opcional no perfil. Quando a cobrança
                automática por WhatsApp for utilizada, estes
                dados serão usados no template de cobrança.
              </div>
            </section>

            <section className="pro-panel pro-panel--locked">
              <div className="pro-panel__head">
                <div>
                  <span>Conta</span>
                  <h2>Conta e assinatura</h2>

                  <p>
                    Informações contratuais e de acesso.
                    Esses dados não podem ser alterados
                    nesta tela.
                  </p>
                </div>

                <div className="pro-mode pro-mode--locked">
                  Somente leitura
                </div>
              </div>

              <div className="pro-form">
                <LockedField
                  label="E-mail"
                  value={account.email}
                  icon={<Mail size={18} />}
                  helper={
                    account.emailConfirmado
                      ? "E-mail confirmado"
                      : "E-mail pendente de confirmação"
                  }
                />

                <LockedField
                  label="Plano atual"
                  value={account.plano}
                  icon={<Sparkles size={18} />}
                  helper={
                    account.valorPlano
                      ? `R$ ${account.valorPlano}/mês`
                      : "Valor não informado"
                  }
                />

                <LockedField
                  label="Tipo de conta"
                  value={translateAccountType(
                    account.tipoUsuario
                  )}
                  icon={<UserRound size={18} />}
                />

                <LockedField
                  label="Permissão"
                  value={translatePermission(
                    account.permissao
                  )}
                  icon={<ShieldCheck size={18} />}
                />

                <LockedField
                  label="Situação da assinatura"
                  value={translateStatus(
                    account.statusAssinatura
                  )}
                  icon={
                    account.assinaturaAtiva ? (
                      <CheckCircle2 size={18} />
                    ) : (
                      <CircleDollarSign
                        size={18}
                      />
                    )
                  }
                  helper={
                    account.assinaturaAtiva
                      ? "Assinatura com acesso liberado"
                      : "Assinatura sem acesso liberado"
                  }
                />

                <LockedField
                  label="Período de teste"
                  value={
                    account.isTrial
                      ? "Ativo"
                      : "Não ativo"
                  }
                  icon={<Clock3 size={18} />}
                  helper={
                    account.isTrial
                      ? `Término: ${formatDate(
                          account.trialEndsAt
                        )}`
                      : undefined
                  }
                />

                <LockedField
                  label="Próximo vencimento"
                  value={formatDate(
                    account.vencimentoAssinatura ||
                      account.currentPeriodEnd
                  )}
                  icon={
                    <CalendarDays size={18} />
                  }
                />

                <LockedField
                  label="Data do cadastro"
                  value={formatDate(
                    account.dataCadastro
                  )}
                  icon={
                    <CalendarDays size={18} />
                  }
                />

                <LockedField
                  label="Termos de uso"
                  value={
                    account.termosAceitos
                      ? "Aceitos"
                      : "Não aceitos"
                  }
                  icon={<FileCheck2 size={18} />}
                  helper={
                    account.termosAceitos
                      ? `Versão ${
                          account.termosVersao ||
                          "não informada"
                        } • ${formatDate(
                          account.termosAceitosEm
                        )}`
                      : undefined
                  }
                />

                <LockedField
                  label="Origem do cadastro"
                  value={translateOrigin(
                    account.origemCadastro
                  )}
                  icon={
                    <BadgeCheck size={18} />
                  }
                />
              </div>
            </section>

            <section className="pro-actions">
              <button
                className="pro-btn pro-btn--ghost-dark"
                type="button"
                onClick={goBack}
                disabled={saving}
              >
                <ArrowLeft size={16} />
                Cancelar
              </button>

              <button
                className="pro-btn pro-btn--primary"
                type="button"
                onClick={saveProfile}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <LoaderCircle size={16} className="pro-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Salvar alterações
                  </>
                )}
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}